// MODE.B1 (#1174) — Delivery Mode picker.
//
// Extracted from NewProjectModal so it can be reused unmodified by
// BrownfieldOnboardingPage (Step 0 inline section). Both surfaces share
// the same visual treatment, ARIA semantics, and checkpoint toggle UI.
//
// State is owned by the parent (controlled component). The picker accepts:
//   • value / onChange                — current mode + setter
//   • checkpoints / onCheckpointsChange — Set<string> of selected
//                                         checkpoint keys + setter
//   • waivedPhases (optional)         — phases the parent has marked as
//                                         waived. B1 ships the prop but
//                                         does not render anything with
//                                         it; MODE.B3 will use it to
//                                         render an inline note next to
//                                         the matching checkpoint
//                                         (operationally moot once the
//                                         waiver passes the gate). Keeps
//                                         the API stable across B1→B3 so
//                                         B3 doesn't need to widen the
//                                         interface.
//
// Default checkpoint profile (Phase 1 / 5 / 7 EXIT) is exported as
// DEFAULT_CHECKPOINTS for consumers that need to seed the initial Set;
// `defaultCheckpointKeys()` returns that initial Set ready to drop into
// useState.

import type { CSSProperties } from 'react';
import { HelpPopover } from './help';

export interface DefaultCheckpoint {
  phase:    'phase_1_requirements' | 'phase_5_code_review' | 'phase_7_deployment';
  gateType: 'EXIT';
  label:    string;
  helper:   string;
}

export const DEFAULT_CHECKPOINTS: readonly DefaultCheckpoint[] = [
  {
    phase:    'phase_1_requirements',
    gateType: 'EXIT',
    label:    'Phase 1 EXIT — Requirements sign-off',
    helper:   'Pause for human review before agents move from PRD/user-stories work into Architecture.',
  },
  {
    phase:    'phase_5_code_review',
    gateType: 'EXIT',
    label:    'Phase 5 EXIT — Code Review sign-off',
    helper:   'Pause for human review before merging out of Code Review into Testing.',
  },
  {
    phase:    'phase_7_deployment',
    gateType: 'EXIT',
    label:    'Phase 7 EXIT — Deployment sign-off',
    helper:   'Pause for human authorisation before agents close out the deployment phase.',
  },
] as const;

export function checkpointKey(c: Pick<DefaultCheckpoint, 'phase' | 'gateType'>): string {
  return `${c.phase}|${c.gateType}`;
}

export function defaultCheckpointKeys(): Set<string> {
  return new Set(DEFAULT_CHECKPOINTS.map(checkpointKey));
}

export interface DeliveryModePickerProps {
  value: 'governed' | 'accelerate';
  onChange: (mode: 'governed' | 'accelerate') => void;
  checkpoints: Set<string>;
  onCheckpointsChange: (next: Set<string>) => void;
  /**
   * Phases the parent has marked as waived (e.g. brownfield wizard's Prior
   * Phases step). MODE.B1 accepts the prop but does not render with it;
   * MODE.B3 will surface a "checkpoint is operationally moot for waived
   * phases" note next to matching entries. Defined here so B3 doesn't
   * widen the picker API.
   */
  waivedPhases?: readonly string[];
  /** Optional override style merged into the outer container. */
  style?: CSSProperties;
}

export default function DeliveryModePicker({
  value,
  onChange,
  checkpoints,
  onCheckpointsChange,
  // MODE.B3 (#1176) — when the parent (BrownfieldOnboardingPage) passes a
  // waived-phase list, render an informational note next to each checkpoint
  // whose phase appears in the list. NewProjectModal does NOT pass this
  // prop (greenfield has no prior-phase waivers); `?? false` in
  // isPhaseWaived makes that case a no-op so the greenfield render is
  // unchanged.
  waivedPhases,
  style,
}: DeliveryModePickerProps) {
  const isCheckpointSelected = (c: DefaultCheckpoint) => checkpoints.has(checkpointKey(c));
  const toggleCheckpoint = (c: DefaultCheckpoint) => {
    const next = new Set(checkpoints);
    const k = checkpointKey(c);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    onCheckpointsChange(next);
  };
  const minCheckpointGuard = value === 'accelerate' && checkpoints.size === 0;
  // B3 — informational only. The note tells the user the checkpoint won't
  // fire (the prior-phase waiver passes the gate before the checkpoint is
  // reached); it does NOT disable the toggle, auto-deselect the checkpoint,
  // or affect the min-1 guard.
  const isPhaseWaived = (c: DefaultCheckpoint) =>
    waivedPhases?.includes(c.phase) ?? false;

  return (
    <div
      data-testid="delivery-mode-section"
      style={{
        padding: 16,
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--surface-alt)',
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          Delivery Mode
        </div>
        <HelpPopover
          id="delivery-mode-overview"
          variant="info"
          heading="Choosing delivery mode"
          body={
            <>
              <p style={{ margin: '0 0 8px' }}>
                Both modes use the same gate criteria. The difference is whether a human is invited to look before each phase transition.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>Governed Mode</strong> pauses for human review at every phase exit. You confirm each transition, even when gates pass. Best for first projects, regulated workloads, and any situation where review depth matters more than speed.
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>Accelerate Mode</strong> lets the Phase Gate Agent transition phases autonomously when gates pass — except at Review Checkpoints you select below. Faster, but a wrong autonomous transition costs more to reverse than a paused-and-reviewed one.
              </p>
              <p style={{ margin: 0 }}>
                You can change modes mid-project. The change applies to the next phase transition; any in-progress transition completes under the original mode.
              </p>
            </>
          }
        />
      </div>
      <p
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 11,
          color: 'var(--text-secondary)',
          marginBottom: 12,
          lineHeight: 1.5,
        }}
      >
        Governed Mode pauses for human review at every phase exit. Accelerate Mode lets the Phase Gate Agent transition phases autonomously when their gates pass — except at Review Checkpoints you select below.
      </p>

      {/* Explicit role="radiogroup" so screen readers announce the
          mutual-exclusion relationship between Governed and Accelerate. */}
      <div
        role="radiogroup"
        aria-label="Delivery Mode"
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        <button
          type="button"
          role="radio"
          aria-checked={value === 'governed'}
          data-testid="delivery-mode-governed"
          onClick={() => onChange('governed')}
          style={{
            textAlign:    'left',
            padding:      12,
            borderRadius: 'var(--radius-sm)',
            border:       value === 'governed'
              ? '2px solid var(--primary)'
              : '1px solid var(--border)',
            background:   'var(--surface)',
            cursor:       'pointer',
            outline:      'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Governed
            </span>
            <span
              style={{
                fontFamily:    'var(--font-body)',
                fontSize:      10,
                fontWeight:    600,
                padding:       '2px 6px',
                borderRadius:  10,
                background:    'rgba(124,58,237,0.10)',
                color:         'var(--primary)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Default
            </span>
          </div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
            Every phase exit waits for human approval. Recommended for first projects and regulated workloads.
          </div>
        </button>

        <button
          type="button"
          role="radio"
          aria-checked={value === 'accelerate'}
          data-testid="delivery-mode-accelerate"
          onClick={() => onChange('accelerate')}
          style={{
            textAlign:    'left',
            padding:      12,
            borderRadius: 'var(--radius-sm)',
            border:       value === 'accelerate'
              ? '2px solid var(--signal)'
              : '1px solid var(--border)',
            background:   'var(--surface)',
            cursor:       'pointer',
            outline:      'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Accelerate
            </span>
            <span
              style={{
                fontFamily:    'var(--font-body)',
                fontSize:      10,
                fontWeight:    600,
                padding:       '2px 6px',
                borderRadius:  10,
                background:    'rgba(0,229,160,0.12)',
                color:         'var(--signal)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Track B
            </span>
          </div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
            Phase Gate Agent autonomously transitions phases when their gates pass. Pauses at the Review Checkpoints you select.
          </div>
        </button>
      </div>

      {value === 'accelerate' && (
        <div
          data-testid="checkpoint-toggles"
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid var(--border)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text)',
              marginBottom: 8,
            }}
          >
            Review Checkpoints
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {DEFAULT_CHECKPOINTS.map((c) => {
              const checked = isCheckpointSelected(c);
              return (
                <label
                  key={checkpointKey(c)}
                  data-testid={`checkpoint-${c.phase}-${c.gateType}`}
                  style={{
                    display:      'flex',
                    alignItems:   'flex-start',
                    gap:          8,
                    padding:      8,
                    borderRadius: 'var(--radius-sm)',
                    background:   checked ? 'rgba(0,229,160,0.06)' : 'transparent',
                    border:       checked ? '1px solid rgba(0,229,160,0.30)' : '1px solid var(--border)',
                    cursor:       'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleCheckpoint(c)}
                    style={{ marginTop: 2, accentColor: 'var(--signal)' }}
                  />
                  <div>
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                      {c.label}
                    </div>
                    <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {c.helper}
                    </div>
                    {isPhaseWaived(c) && (
                      // R2 finding 2 (a11y): aria-hidden so the note text is
                      // NOT concatenated into the checkbox's accessible name.
                      // The note is visible to sighted users; the row's
                      // checked/unchecked semantics are unchanged for screen-
                      // reader users. A richer aria-describedby treatment is
                      // a candidate follow-on but would require restructuring
                      // the existing helper-text-inside-label pattern, which
                      // is out of B3's scope.
                      <div
                        aria-hidden="true"
                        data-testid={`checkpoint-waived-note-${c.phase}`}
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontSize:   11,
                          // R2 finding 1: use the --warning token instead of a
                          // hand-rolled rgb literal so the note matches every
                          // other amber callout in the product.
                          color:      'var(--warning)',
                          background: 'rgba(245,158,11,0.10)',
                          border:     '1px solid rgba(245,158,11,0.30)',
                          borderRadius: 'var(--radius-sm)',
                          padding:    '4px 8px',
                          marginTop:  6,
                          lineHeight: 1.4,
                          // #1266: lay out [icon][text] so the note reads as a
                          // distinct amber callout rather than a second line of
                          // helper text. Still aria-hidden (B3.4) — the icon and
                          // the bold lead clause are sighted-only emphasis.
                          display:     'flex',
                          gap:         6,
                          alignItems:  'flex-start',
                        }}
                      >
                        {/* Hand-rolled amber warning triangle (NOT lucide — keeps
                            this shared component free of new imports). Copies the
                            DevReflectionCard.tsx precedent, shrunk to 13x13.
                            stroke rgb(245,158,11) == var(--warning). */}
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="rgb(245, 158, 11)"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          style={{ flexShrink: 0, marginTop: 1 }}
                        >
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        {/* minWidth:0 lets the text flex child shrink/wrap
                            below its longest word at narrow modal widths. */}
                        <span style={{ minWidth: 0 }}>
                          <strong style={{ fontWeight: 600 }}>This phase was waived</strong> &mdash; the checkpoint won&apos;t fire (the waiver passes the gate before the checkpoint is reached).
                        </span>
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          {minCheckpointGuard && (
            <div
              role="alert"
              data-testid="min-checkpoint-warning"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 11,
                color: 'var(--error)',
                marginTop: 8,
              }}
            >
              Select at least one Review Checkpoint, or switch to Governed Mode.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
